import { useCallback, useEffect, useState } from 'react';
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  LeftOutlined,
  PlusOutlined,
  QrcodeOutlined,
  RightOutlined,
  SmileOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Drawer,
  Empty,
  Input,
  Modal,
  Select,
  Spin,
  Steps,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';

import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import { useScreen } from 'hooks/useScreen';

import { queryErrorMessage } from '../ClubRegistration/types';

const { Text, Title } = Typography;

interface ClassOption {
  id: number;
  name: string;
  class_number: number | null;
}

interface StudentRow {
  id: number;
  full_name: string;
  code: string;
  card_balance: number;
  price: number | null;
  // How the student already has a Paid lunch for the chosen day, if they do.
  paid_channel: string | null;
  // For a CLASS paid_channel, the class that recorded it (null when the
  // recording isn't on lunch_order yet).
  paid_class_id: number | null;
  paid_class_name: string | null;
}

// RECORDED: the class has a Class lunch recording (CLASS Lunch orders) that day.
type DayStatus = 'NONE' | 'RECORDED';

interface WeekDay {
  date: string;
  has_menu: boolean;
  lunch_menu_id: number | null;
  status: DayStatus;
  can_create: boolean;
  eaten_count: number;
  // Class students who paid this day's lunch at the kiosk (QR/NFC/KIOSK).
  qr_count: number;
  // Class students whose kiosk Lunch order is still Pending in the kitchen.
  pending_order_count: number;
  reversed_count: number;
  // The class's CLASS orders and its current students' kiosk orders.
  orders: DayOrder[];
}

type CorrectionStatus = 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED';

interface DayOrder {
  id: number;
  student_id: number;
  student_name: string;
  student_code: string | null;
  student_card_balance: number;
  student_phone: string | null;
  channel: 'CLASS' | 'KIOSK';
  status: 'COMPLETED' | 'REVERSED';
  correction_status: CorrectionStatus | null;
  correction_reason: string | null;
}

interface CreateResult {
  recorded: number;
  skipped: number;
  // Students whose lunch couldn't be charged (e.g. no meal price); no order.
  failed: { student_id: number; error: string }[];
}

function reportCreateResult(result: CreateResult | undefined) {
  if (!result || result.failed.length === 0) {
    message.success('Үдийн хоолны бүртгэл амжилттай үүслээ.');
    return;
  }
  message.warning(
    `${result.recorded} сурагч бүртгэгдлээ, ${result.failed.length} сурагч бүртгэгдсэнгүй: ${result.failed[0].error}`,
  );
}

const WEEKDAY_LABELS = ['Даваа', 'Мягмар', 'Лхагва', 'Пүрэв', 'Баасан'];

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

// Class lunch is charged even below the Card minimum (CONTEXT.md), so the
// balance is only shown in the warning color; it never blocks recording.
function balanceTextType(s: StudentRow): 'warning' | 'secondary' {
  return s.card_balance - (s.price ?? 0) < 0 ? 'warning' : 'secondary';
}

// Already has that day's lunch (kiosk, a Pending kiosk order, or a class
// lunch recording): shown in place of the switch, so the student can't be
// selected.
function paidTag(s: StudentRow, classId: number | undefined) {
  const channel = s.paid_channel;
  if (channel === 'PENDING') {
    return (
      <Tag icon={<ClockCircleOutlined />} style={{ marginInlineEnd: 0 }}>
        Хүлээгдэж буй
      </Tag>
    );
  }
  if (channel === 'CLASS') {
    const label =
      s.paid_class_id === null
        ? 'Ангиар бүртгэгдсэн'
        : s.paid_class_id === classId
          ? 'Энэ ангид бүртгэгдсэн'
          : `${s.paid_class_name ?? 'Өөр'} ангид бүртгэгдсэн`;
    return <Tag style={{ marginInlineEnd: 0 }}>{label}</Tag>;
  }
  return (
    <Tag icon={<QrcodeOutlined />} style={{ marginInlineEnd: 0 }}>
      {channel === 'QR' || channel === 'NFC' || channel === 'KIOSK' ? 'QR уншуулсан' : 'Хоол авсан'}
    </Tag>
  );
}

function formatAmount(n: number): string {
  return `${new Intl.NumberFormat('mn-MN').format(n)}₮`;
}

const ManagedClassLunch = () => {
  const { level, runQuery } = useScreen();
  // Recording and correction requests are edit-level Queries; the
  // server refuses them for view anyway.
  const canEdit = level === 'edit';
  const [expandedTxId, setExpandedTxId] = useState<number | null>(null);

  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classesError, setClassesError] = useState<string | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<number>();

  const [weekOffset, setWeekOffset] = useState(0);
  const [weekDays, setWeekDays] = useState<WeekDay[]>([]);
  const [weekLoading, setWeekLoading] = useState(false);
  const [classStudentCount, setClassStudentCount] = useState<number | null>(null);

  // Wizard (create) state
  const [wizardDay, setWizardDay] = useState<WeekDay | null>(null);
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardStudents, setWizardStudents] = useState<(StudentRow & { selected: boolean })[]>([]);
  const [wizardLoading, setWizardLoading] = useState(false);
  const [wizardSubmitting, setWizardSubmitting] = useState(false);

  // Adjustment (add missed students to an already-recorded day) state
  const [adjustDay, setAdjustDay] = useState<WeekDay | null>(null);
  const [adjustStep, setAdjustStep] = useState(0);
  const [adjustStudents, setAdjustStudents] = useState<(StudentRow & { selected: boolean })[]>([]);
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [adjustSubmitting, setAdjustSubmitting] = useState(false);

  // Засвар хүсэх (correction request) state
  const [correctionModalTxId, setCorrectionModalTxId] = useState<number | null>(null);
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);

  useEffect(() => {
    setClassesLoading(true);
    runQuery<ClassOption>('class_options')
      .then((allItems) => {
        setClasses(allItems);
        if (allItems.length === 0) {
          setClassesError('Танд харьяалагдах анги алга байна.');
        }
      })
      .catch((e) => setClassesError(queryErrorMessage(e)))
      .finally(() => setClassesLoading(false));
  }, [runQuery]);

  const loadWeek = useCallback(() => {
    if (!selectedClassId) {
      setWeekDays([]);
      return;
    }
    setWeekLoading(true);
    runQuery<WeekDay>('class_lunch_week', { classId: selectedClassId, weekOffset })
      .then(setWeekDays)
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setWeekLoading(false));
  }, [selectedClassId, weekOffset, runQuery]);

  useEffect(() => {
    loadWeek();
  }, [loadWeek]);

  useEffect(() => {
    if (!selectedClassId) {
      setClassStudentCount(null);
      return;
    }
    runQuery<StudentRow>('class_lunch_students', { classId: selectedClassId })
      .then((rows) => setClassStudentCount(rows.length))
      .catch(() => setClassStudentCount(null));
  }, [selectedClassId, runQuery]);

  function openWizard(day: WeekDay) {
    if (!selectedClassId) return;
    setWizardDay(day);
    setWizardStep(0);
    setWizardLoading(true);
    runQuery<StudentRow>('class_lunch_students', {
      classId: selectedClassId,
      lunchMenuId: day.lunch_menu_id ?? undefined,
    })
      .then((rows) => setWizardStudents(rows.map((s) => ({ ...s, selected: false }))))
      .catch((e) => {
        message.error(queryErrorMessage(e));
        setWizardDay(null);
      })
      .finally(() => setWizardLoading(false));
  }

  function closeWizard() {
    setWizardDay(null);
    setWizardStudents([]);
    setWizardStep(0);
  }

  function openAdjustment(day: WeekDay) {
    if (!selectedClassId) return;
    setAdjustDay(day);
    setAdjustStep(0);
    setAdjustLoading(true);
    runQuery<StudentRow>('class_lunch_students', {
      classId: selectedClassId,
      lunchMenuId: day.lunch_menu_id ?? undefined,
    })
      .then((rows) => {
        // Буцаагдсан (REVERSED) сурагчийг дахин нэмэх боломжтой байлгана.
        const alreadyRecordedIds = new Set(
          day.orders.filter((t) => t.status !== 'REVERSED').map((t) => t.student_id),
        );
        const remaining = rows.filter((s) => !alreadyRecordedIds.has(s.id));
        if (remaining.length === 0) {
          message.info('Бүх сурагч аль хэдийн бүртгэгдсэн байна.');
          setAdjustDay(null);
          return;
        }
        setAdjustStudents(remaining.map((s) => ({ ...s, selected: false })));
      })
      .catch((e) => {
        message.error(queryErrorMessage(e));
        setAdjustDay(null);
      })
      .finally(() => setAdjustLoading(false));
  }

  function closeAdjustment() {
    setAdjustDay(null);
    setAdjustStudents([]);
    setAdjustStep(0);
  }

  function toggleAdjustStudent(id: number, selected: boolean) {
    setAdjustStudents((prev) => prev.map((s) => (s.id === id ? { ...s, selected } : s)));
  }

  function toggleAllAdjustStudents(selected: boolean) {
    setAdjustStudents((prev) => prev.map((s) => ({ ...s, selected: selected && !s.paid_channel })));
  }

  async function submitAdjustment() {
    if (!adjustDay || !selectedClassId) return;
    const studentIds = adjustStudents.filter((s) => s.selected).map((s) => s.id);
    if (studentIds.length === 0) {
      message.warning('Хамгийн багадаа нэг сурагч сонгоно уу.');
      return;
    }

    setAdjustSubmitting(true);
    try {
      const [result] = await runQuery<CreateResult>('class_lunch_create', {
        classId: selectedClassId,
        lunchMenuId: adjustDay.lunch_menu_id,
        studentIds,
      });
      reportCreateResult(result);
      closeAdjustment();
      loadWeek();
    } catch (e) {
      message.error(queryErrorMessage(e));
    } finally {
      setAdjustSubmitting(false);
    }
  }

  function submitAdjustmentWithWarning() {
    Modal.confirm({
      title: 'Баталгаажуулах уу?',
      content: 'Бүртгэсний дараа гүйлгээг өөрчлөх боломжгүй тул анхаарна уу.',
      okText: 'Баталгаажуулах',
      cancelText: 'Болих',
      onOk: submitAdjustment,
    });
  }

  function toggleStudent(id: number, selected: boolean) {
    setWizardStudents((prev) => prev.map((s) => (s.id === id ? { ...s, selected } : s)));
  }

  function toggleAllStudents(selected: boolean) {
    setWizardStudents((prev) => prev.map((s) => ({ ...s, selected: selected && !s.paid_channel })));
  }

  async function confirmWizard() {
    if (!wizardDay || !selectedClassId) return;
    const studentIds = wizardStudents.filter((s) => s.selected).map((s) => s.id);
    if (studentIds.length === 0) {
      message.warning('Хамгийн багадаа нэг сурагч сонгоно уу.');
      return;
    }

    setWizardSubmitting(true);
    try {
      const [result] = await runQuery<CreateResult>('class_lunch_create', {
        classId: selectedClassId,
        lunchMenuId: wizardDay.lunch_menu_id,
        studentIds,
      });
      reportCreateResult(result);
      closeWizard();
      loadWeek();
    } catch (e) {
      message.error(queryErrorMessage(e));
    } finally {
      setWizardSubmitting(false);
    }
  }

  function confirmWizardWithWarning() {
    Modal.confirm({
      title: 'Баталгаажуулах уу?',
      content: 'Бүртгэсний дараа гүйлгээг өөрчлөх боломжгүй тул анхаарна уу.',
      okText: 'Баталгаажуулах',
      cancelText: 'Болих',
      onOk: confirmWizard,
    });
  }

  function openCorrectionModal(transactionId: number) {
    setCorrectionModalTxId(transactionId);
    setCorrectionReason('');
  }

  function closeCorrectionModal() {
    setCorrectionModalTxId(null);
    setCorrectionReason('');
  }

  async function submitCorrectionRequest() {
    if (!correctionModalTxId) return;
    const reason = correctionReason.trim();
    if (!reason) {
      message.warning('Шалтгаанаа бичнэ үү.');
      return;
    }
    setCorrectionSubmitting(true);
    try {
      await runQuery('lunch_correction_request', {
        orderId: correctionModalTxId,
        reason,
      });
      message.success('Засварын хүсэлт илгээгдлээ.');
      closeCorrectionModal();
      loadWeek();
    } catch (e) {
      message.error(queryErrorMessage(e));
    } finally {
      setCorrectionSubmitting(false);
    }
  }

  const selectedCount = wizardStudents.filter((s) => s.selected).length;
  const wizardSelectableCount = wizardStudents.filter((s) => !s.paid_channel).length;
  const allStudentsSelected = wizardSelectableCount > 0 && selectedCount === wizardSelectableCount;
  const wizardClassName = classes.find((c) => c.id === selectedClassId)?.name;
  const wizardDayLabel = wizardDay
    ? `${WEEKDAY_LABELS[new Date(wizardDay.date).getDay() - 1]} (${formatDate(wizardDay.date)})`
    : '';

  const adjustSelectedCount = adjustStudents.filter((s) => s.selected).length;
  const adjustSelectableCount = adjustStudents.filter((s) => !s.paid_channel).length;
  const allAdjustSelected =
    adjustSelectableCount > 0 && adjustSelectedCount === adjustSelectableCount;
  const adjustClassName = classes.find((c) => c.id === selectedClassId)?.name;
  const adjustDayLabel = adjustDay
    ? `${WEEKDAY_LABELS[new Date(adjustDay.date).getDay() - 1]} (${formatDate(adjustDay.date)})`
    : '';

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card>
        {!classesLoading && classesError && (
          <Alert type="warning" showIcon message={classesError} style={{ marginBottom: 24 }} />
        )}

        {!classesError && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center' }}>
            <Select
              placeholder="Анги сонгох"
              style={{ width: 320 }}
              loading={classesLoading}
              value={selectedClassId}
              onChange={(value) => setSelectedClassId(value)}
              allowClear
              onClear={() => setSelectedClassId(undefined)}
              options={classes.map((c) => ({
                value: c.id,
                label: `${c.name} (${c.class_number}-р анги)`,
              }))}
            />

            <div style={{ display: 'flex', gap: 12, marginLeft: 'auto' }}>
              <Button icon={<LeftOutlined />} onClick={() => setWeekOffset((w) => w - 1)}>
                Өмнөх
              </Button>
              <Button onClick={() => setWeekOffset(0)} disabled={weekOffset === 0}>
                Энэ долоо хоног
              </Button>
              <Button onClick={() => setWeekOffset((w) => w + 1)}>
                Дараагийн<RightOutlined />
              </Button>
            </div>
          </div>
        )}

        {!classesError && !selectedClassId && (
          <Card>
            <Empty description="Эхлээд анги сонгоно уу." />
          </Card>
        )}

        {selectedClassId && weekLoading && <Spin />}

        {selectedClassId && !weekLoading && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 16 }}>
            {weekDays.map((day, i) => {
              const activeTxCount = day.orders.filter((t) => t.status !== 'REVERSED').length;

              return (
                <Card
                  key={day.date}
                  size="small"
                  title={`${WEEKDAY_LABELS[i]} (${formatDate(day.date)})`}
                  extra={
                    canEdit && day.has_menu && day.status !== 'NONE' ? (
                      <Button
                        size="small"
                        type="text"
                        icon={<PlusOutlined />}
                        title={
                          classStudentCount != null && activeTxCount >= classStudentCount
                            ? 'Бүх сурагч бүртгэгдсэн байна'
                            : 'Дутсан сурагч нэмэх'
                        }
                        disabled={
                          classStudentCount != null && activeTxCount >= classStudentCount
                        }
                        onClick={() => openAdjustment(day)}
                      />
                    ) : null
                  }
                  style={{ backgroundColor: '#fafafa' }}
                  styles={{ body: { padding: 0 }, header: { backgroundColor: '#f5f5f5' } }}
                >
                  {!day.has_menu && (
                    <Text type="secondary" style={{ display: 'block', padding: 12 }}>
                      Цэс байхгүй
                    </Text>
                  )}

                  {canEdit && day.has_menu && day.status === 'NONE' && day.can_create && (
                    <div style={{ padding: 12 }}>
                      <Button type="primary" block onClick={() => openWizard(day)}>
                        Үүсгэх
                      </Button>
                    </div>
                  )}

                  {day.has_menu && day.status === 'NONE' && !day.can_create && (
                    <Text type="secondary" style={{ display: 'block', padding: 12 }}>
                      Ирээдүйн өдөр
                    </Text>
                  )}

                  {day.orders.length > 0 && (
                    <div>
                      {day.orders.map((t) => {
                        const isExpanded = expandedTxId === t.id;
                        return (
                          <div key={t.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 8,
                                padding: '8px 12px',
                                cursor: 'pointer',
                                background: isExpanded ? '#fafafa' : undefined,
                              }}
                              onClick={() =>
                                setExpandedTxId((prev) => (prev === t.id ? null : t.id))
                              }
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  minWidth: 0,
                                }}
                              >
                                {t.status === 'COMPLETED' && (
                                  <CheckCircleFilled style={{ color: '#52c41a', flexShrink: 0 }} />
                                )}
                                {t.status === 'REVERSED' && (
                                  <UndoOutlined style={{ color: '#1677ff', flexShrink: 0 }} />
                                )}
                                <Text ellipsis style={{ maxWidth: '100%' }}>
                                  {t.student_name}
                                </Text>
                                {t.channel === 'KIOSK' && (
                                  <Tag icon={<QrcodeOutlined />} style={{ flexShrink: 0 }}>
                                    Киоск
                                  </Tag>
                                )}
                                {t.correction_status === 'PENDING_APPROVAL' && (
                                  <Tag color="processing" style={{ flexShrink: 0 }}>
                                    Засвар хүлээгдэж буй
                                  </Tag>
                                )}
                              </div>

                            </div>

                            {isExpanded && (
                              <div
                                style={{
                                  padding: '4px 12px 10px 32px',
                                  background: '#fafafa',
                                }}
                              >
                                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                                  Код: {t.student_code ?? '—'}
                                </Text>
                                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                                  Картын үлдэгдэл: {t.student_card_balance}
                                </Text>
                                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                                  Утас: {t.student_phone ?? '—'}
                                </Text>

                                {t.status === 'REVERSED' && (
                                  <Text style={{ display: 'block', fontSize: 12, color: '#1677ff' }}>
                                    Буцаагдсан — шалтгаан: {t.correction_reason ?? '—'}
                                  </Text>
                                )}
                                {t.correction_status === 'PENDING_APPROVAL' && (
                                  <Text style={{ display: 'block', fontSize: 12, color: '#1677ff' }}>
                                    Засвар хүсэлт хяналтад: {t.correction_reason}
                                  </Text>
                                )}
                                {t.correction_status === 'REJECTED' && (
                                  <Text type="danger" style={{ display: 'block', fontSize: 12 }}>
                                    Засвар хүсэлт татгалзагдсан.
                                  </Text>
                                )}
                                {canEdit &&
                                  t.status === 'COMPLETED' &&
                                  t.correction_status !== 'PENDING_APPROVAL' && (
                                    <Button
                                      size="small"
                                      style={{ marginTop: 8 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openCorrectionModal(t.id);
                                      }}
                                    >
                                      Засвар хүсэх
                                    </Button>
                                  )}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {day.status === 'RECORDED' && (
                        <div
                          style={{
                            padding: '8px 12px',
                            display: 'flex',
                            gap: 6,
                            flexWrap: 'wrap',
                          }}
                        >
                          {day.eaten_count > 0 && (
                            <Tag color="success" icon={<CheckCircleFilled />}>
                              {day.eaten_count} амжилттай
                            </Tag>
                          )}
                          {day.qr_count > 0 && (
                            <Tag icon={<QrcodeOutlined />}>
                              {day.qr_count} QR уншуулсан
                            </Tag>
                          )}
                          {day.pending_order_count > 0 && (
                            <Tag icon={<ClockCircleOutlined />}>
                              {day.pending_order_count} хүлээгдэж буй
                            </Tag>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {day.status === 'NONE' && (day.qr_count > 0 || day.pending_order_count > 0) && (
                    <div style={{ padding: '8px 12px' }}>
                      {day.qr_count > 0 && (
                        <Tag icon={<QrcodeOutlined />}>{day.qr_count} QR уншуулсан</Tag>
                      )}
                      {day.pending_order_count > 0 && (
                        <Tag icon={<ClockCircleOutlined />}>
                          {day.pending_order_count} хүлээгдэж буй
                        </Tag>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </Card>

      {/* Үүсгэх wizard */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span>Үдийн хоол бүртгэх</span>
            <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
              {wizardClassName} · {wizardDayLabel}
            </Text>
          </div>
        }
        open={!!wizardDay}
        onClose={closeWizard}
        placement="right"
        width={720}
        destroyOnClose
        extra={
          !wizardLoading && wizardStep === 0 ? (
            <Button type="primary" disabled={selectedCount === 0} onClick={() => setWizardStep(1)}>
              Дараах
            </Button>
          ) : !wizardLoading && wizardStep === 1 ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={() => setWizardStep(0)}>Буцах</Button>
              <Button type="primary" loading={wizardSubmitting} onClick={confirmWizardWithWarning}>
                Баталгаажуулах
              </Button>
            </div>
          ) : null
        }
      >
        <Steps
          current={wizardStep}
          size="small"
          style={{ marginBottom: 24 }}
          items={[{ title: 'Сурагч сонгох' }, { title: 'Баталгаажуулах' }]}
        />

        {wizardLoading && <Spin />}

        {!wizardLoading && wizardStep === 0 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Text type="secondary">{selectedCount} сурагч сонгогдсон</Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text>Бүгдийн сонгох</Text>
                <Switch
                  checked={allStudentsSelected}
                  disabled={wizardSelectableCount === 0}
                  onChange={toggleAllStudents}
                  checkedChildren={
                    <>
                      <SmileOutlined />&nbsp;lunch
                    </>
                  }
                  style={{ minWidth: 72 }}
                />
              </div>
            </div>
            <div>
              {wizardStudents.map((s) => (
                <div
                  key={s.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>
                      {s.full_name} ({s.code})
                    </span>
                    <Text type={balanceTextType(s)} style={{ fontSize: 12 }}>
                      Үлдэгдэл: {formatAmount(s.card_balance)}
                    </Text>
                  </div>
                  {s.paid_channel ? (
                    paidTag(s, selectedClassId)
                  ) : (
                    <Switch
                      checked={s.selected}
                      onChange={(v) => toggleStudent(s.id, v)}
                      checkedChildren={
                        <>
                          <SmileOutlined />&nbsp;lunch
                        </>
                      }
                      style={{ minWidth: 72 }}
                    />
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {!wizardLoading && wizardStep === 1 && (
          <>
            <Title level={5}>Нийт {selectedCount} сурагч хооллоно</Title>
            <div>
              {wizardStudents
                .filter((s) => s.selected)
                .map((s) => (
                  <div
                    key={s.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <span>
                      {s.full_name} ({s.code})
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Text type={balanceTextType(s)}>({formatAmount(s.card_balance)})</Text>
                      <Text>{s.price != null ? formatAmount(s.price) : '—'}</Text>
                    </div>
                  </div>
                ))}
            </div>
          </>
        )}
      </Drawer>

      {/* Adjustment drawer — add students missed in the original registration */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span>Дутсан сурагч нэмэх</span>
            <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
              {adjustClassName} · {adjustDayLabel}
            </Text>
          </div>
        }
        open={!!adjustDay}
        onClose={closeAdjustment}
        placement="right"
        width={720}
        destroyOnClose
        extra={
          !adjustLoading && adjustStep === 0 ? (
            <Button
              type="primary"
              disabled={adjustSelectedCount === 0}
              onClick={() => setAdjustStep(1)}
            >
              Дараах
            </Button>
          ) : !adjustLoading && adjustStep === 1 ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={() => setAdjustStep(0)}>Буцах</Button>
              <Button
                type="primary"
                loading={adjustSubmitting}
                onClick={submitAdjustmentWithWarning}
              >
                Баталгаажуулах
              </Button>
            </div>
          ) : null
        }
      >
        <Steps
          current={adjustStep}
          size="small"
          style={{ marginBottom: 24 }}
          items={[{ title: 'Сурагч сонгох' }, { title: 'Баталгаажуулах' }]}
        />

        {adjustLoading && <Spin />}

        {!adjustLoading && adjustStep === 0 && (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                marginBottom: 12,
              }}
            >
              <Text type="secondary">{adjustSelectedCount} сурагч сонгогдсон</Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text>Бүгдийн сонгох</Text>
                <Switch
                  checked={allAdjustSelected}
                  disabled={adjustSelectableCount === 0}
                  onChange={toggleAllAdjustStudents}
                  checkedChildren={
                    <>
                      <SmileOutlined />&nbsp;lunch
                    </>
                  }
                  style={{ minWidth: 72 }}
                />
              </div>
            </div>
            <div>
              {adjustStudents.map((s) => (
                <div
                  key={s.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>
                      {s.full_name} ({s.code})
                    </span>
                    <Text type={balanceTextType(s)} style={{ fontSize: 12 }}>
                      Үлдэгдэл: {formatAmount(s.card_balance)}
                    </Text>
                  </div>
                  {s.paid_channel ? (
                    paidTag(s, selectedClassId)
                  ) : (
                    <Switch
                      checked={s.selected}
                      onChange={(v) => toggleAdjustStudent(s.id, v)}
                      checkedChildren={
                        <>
                          <SmileOutlined />&nbsp;lunch
                        </>
                      }
                      style={{ minWidth: 72 }}
                    />
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {!adjustLoading && adjustStep === 1 && (
          <>
            <Title level={5}>Нийт {adjustSelectedCount} сурагч хооллоно</Title>
            <div>
              {adjustStudents
                .filter((s) => s.selected)
                .map((s) => (
                  <div
                    key={s.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <span>
                      {s.full_name} ({s.code})
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Text type={balanceTextType(s)}>({formatAmount(s.card_balance)})</Text>
                      <Text>{s.price != null ? formatAmount(s.price) : '—'}</Text>
                    </div>
                  </div>
                ))}
            </div>
          </>
        )}
      </Drawer>

      <Modal
        title="Засвар хүсэх"
        open={!!correctionModalTxId}
        onCancel={closeCorrectionModal}
        onOk={submitCorrectionRequest}
        okText="Илгээх"
        cancelText="Болих"
        confirmLoading={correctionSubmitting}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Буруу бүртгэсэн шалтгаанаа бичнэ үү. Хүсэлт хянагчид очиж, зөвшөөрөгдсөний дараа
          л буцаалт хийгдэнэ.
        </Text>
        <Input.TextArea
          rows={3}
          value={correctionReason}
          onChange={(e) => setCorrectionReason(e.target.value)}
          placeholder="Жишээ нь: буруу сурагч сонгосон"
        />
      </Modal>
    </LayoutWithMenu>
  );
};

export default ManagedClassLunch;
